'use client';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import {
  kLoginRedirectParam,
  kResendVerificationEndpoint,
  kSocialCallbackParam,
  kSocialLoginEndpoint,
  kSocialNetworkParam,
} from '../components/login/constants';
import {
  ILoginResult,
  SocialNetwork,
  SignUpResult,
  IResetPasswordResult,
} from '@ow/shared/login';
import { ServicesHandler } from '../services/service-handler';
import MD5 from 'crypto-js/md5';
import SHA1 from 'crypto-js/sha1';
import SHA256 from 'crypto-js/sha256';
import { useAnalytics } from '../hooks/use-analytics';
import GlobalContext from './global-context';
import { useOverwolfApi } from '../hooks/use-overwolf-api';
import {
  OverwolfProfile,
  isOverwolfAvailable,
  getCurrentOverwolfUser,
  subscribeOverwolfLoginStateChanged,
} from '../services/overwolf/overwolf-api.service';
import {
  requestOverwolfLogout,
  getOverwolfCurrentUser,
} from '../services/overwolf/overwolf-store.service';

import { IProfileResponse } from '../interfaces/services/login-service.interface';
import { LoggerService } from '@ow/shared/services/logger.service';

const log = LoggerService.getCategory('UserProfileContext');

// The Overwolf client needs a moment after a login-state change before
// getCurrentUser reflects the new session, so we delay the re-read.
const OVERWOLF_PROFILE_REFRESH_DELAY_MS = 1500;
export interface SignUpParams {
  username: string;
  email: string;
  password: string;
  captchaV2Response: any;
  sendEmails: boolean;
}
// -----------------------------------------------------------------------------
interface UserProfile {
  profile: GetCurrentUserResponseProfile;
  token: string;
}

interface GetCurrentUserResponseProfile {
  avatar: string;
  developer: boolean;
  email: string;
  expired: boolean;
  nickname: string;
  suspended: boolean;
  username: string;
  unverified: boolean;
}

interface UserProfileContextValue {
  isLoggedIn: boolean;
  isPostLogin: boolean;
  currentUser: UserProfile | null;
  loginStateChangedEvent: any;
  resendVerification: (email: string) => Promise<boolean>;
  getAndSetProfile: (refreshRequired?: boolean) => Promise<IProfileResponse>;
  performOverwolfSignUp: (signUpParams: SignUpParams) => Promise<SignUpResult>;
  performOverwolfLogin: (
    email: string,
    password: string,
  ) => Promise<ILoginResult>;
  performSocialLogin: (network: SocialNetwork) => void;
  logout: () => void;
  resetPassword: (email: string) => Promise<IResetPasswordResult>;
  cancelAccountDeletion: () => Promise<void>;
  setToken: (token: string) => void;
  getUserProfile: (token: string) => Promise<any>;
  getHashedEmailObject?: () => IHashedEmail;
  waitForLoginCheck: () => Promise<void>;
}

// -----------------------------------------------------------------------------
interface IHashedEmail {
  md5: string;
  sha1: string;
  sha256: string;
}

// -----------------------------------------------------------------------------
export const UserProfileContext = createContext<
  UserProfileContextValue | undefined
>(undefined);

// -----------------------------------------------------------------------------
export const UserProfileProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [loginCheckCompleted, setLoginCheckCompleted] =
    useState<boolean>(false);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(null);
  const [isPostLogin, setIsPostLogin] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<UserProfile>(null);
  const [loginStateChangedEvent, setLoginStateChangedEvent] =
    useState<any>(null);

  const { config } = useContext(GlobalContext);
  const { loginService } = ServicesHandler.getInstance(config);
  const analytics = useAnalytics();
  const loginCheckResolvers = useRef<Array<() => void>>([]);

  // When running inside the Overwolf client, the signed-in user comes from the
  // client directly (`overwolf.profile.*`) instead of the REST/cookie login,
  // which fails inside the cross-origin iframe.
  const { isAvailable: isOverwolfClient } = useOverwolfApi();

  // ---------------------------------------------------------------------------
  useLayoutEffect(() => {
    // Skip the REST profile fetch when running inside the Overwolf client; the
    // profile is loaded from the client instead (effect below). This synchronous
    // check avoids firing the cross-origin REST call even once.
    if (isOverwolfAvailable()) {
      return;
    }

    void getAndSetProfile();
  }, []);

  // ---------------------------------------------------------------------------
  // Overwolf client: load the signed-in user from the client once the API is
  // detected, and re-load whenever its login state changes (sign in / out).
  useEffect(() => {
    if (!isOverwolfClient) {
      return;
    }

    void loadOverwolfProfile();

    let refreshTimer: ReturnType<typeof setTimeout>;
    const unsubscribe = subscribeOverwolfLoginStateChanged(() => {
      // The client doesn't have the new session ready the instant the
      // login-state event fires — querying getCurrentUser immediately returns
      // stale data. Give it a moment to settle before re-reading.
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        void loadOverwolfProfile();
      }, OVERWOLF_PROFILE_REFRESH_DELAY_MS);
    });

    return () => {
      clearTimeout(refreshTimer);
      unsubscribe();
    };
  }, [isOverwolfClient]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (loginCheckCompleted) {
      // Resolve all pending promises
      loginCheckResolvers.current.forEach((resolve) => resolve());
      loginCheckResolvers.current = [];
    }
  }, [loginCheckCompleted]);

  // ---------------------------------------------------------------------------
  const getAndSetProfile = async (refreshRequired = false) => {
    const tokenCookie = loginService.getToken();
    if (!tokenCookie) {
      setCurrentUser(null);
      setIsLoggedIn(false);
      setLoginStateChangedEvent({
        status: 'error',
        connectionStatus: 'Offline',
        relayData: null,
        suspended: false,
        token: null,
        username: null,
      });
      setLoginCheckCompleted(true);
      return;
    }

    const profileResult = await getUserProfile(tokenCookie, refreshRequired);
    log.info(`profile result: ${JSON.stringify(profileResult)}`);
    if (profileResult?.status !== 'success') {
      setCurrentUser(null);
      setIsLoggedIn(false);
      setLoginStateChangedEvent({
        status: 'error',
        connectionStatus: 'Offline',
        relayData: null,
        suspended: false,
        token: null,
        username: null,
      });
      setLoginCheckCompleted(true);
      return profileResult.profile;
    }

    if (profileResult?.profile.unverified) {
      setCurrentUser({
        profile:
          profileResult.profile as unknown as GetCurrentUserResponseProfile,
        token: profileResult.token,
      });
      setIsLoggedIn(false);
      setLoginStateChangedEvent({
        status: 'error',
        connectionStatus: 'Offline',
        relayData: null,
        suspended: profileResult.profile.suspended,
        token: profileResult.token,
        username: profileResult.profile.username,
        unverified: profileResult.profile.unverified,
      });
      setLoginCheckCompleted(true);
      return profileResult.profile;
    }

    if (profileResult?.profile.suspended) {
      setCurrentUser({
        profile:
          profileResult.profile as unknown as GetCurrentUserResponseProfile,
        token: profileResult.token,
      });
      setIsLoggedIn(false);
      setLoginStateChangedEvent({
        status: 'success',
        connectionStatus: 'Online',
        relayData: null,
        suspended: profileResult.profile.suspended,
        token: profileResult.token,
        username: profileResult.profile.username,
        unverified: profileResult.profile.unverified,
      });
      setLoginCheckCompleted(true);
      return profileResult.profile;
    }

    setCurrentUser({
      profile:
        profileResult.profile as unknown as GetCurrentUserResponseProfile,
      token: profileResult.token,
    });

    if (profileResult?.profile?.username) {
      analytics.setUserName(profileResult.profile.username);
    }

    const loggedIn = !!profileResult?.profile;
    setIsLoggedIn(loggedIn);

    setLoginStateChangedEvent({
      status: profileResult?.status,
      connectionStatus: loggedIn ? 'Online' : 'Offline',
      relayData: null,
      suspended: profileResult?.profile?.suspended,
      token: profileResult?.token,
      username: profileResult?.profile?.username,
    });

    trackLoginSuccess();
    setLoginCheckCompleted(true);
  };

  // ---------------------------------------------------------------------------
  // Maps the Overwolf client user onto the profile shape the app already uses.
  // `overwolf.profile.getCurrentUser` exposes only email hashes (no plaintext
  // email), so the email is passed in from the store-bridge profile details
  // (see loadOverwolfProfile). The suspended/unverified/developer/expired flags
  // aren't exposed by the client, so those default conservatively.
  const mapOverwolfProfile = (
    user: OverwolfProfile,
    email = '',
  ): GetCurrentUserResponseProfile => ({
    avatar: user.avatar,
    developer: false,
    email,
    expired: false,
    nickname: user.displayName,
    suspended: false,
    username: user.username,
    unverified: false,
  });

  // ---------------------------------------------------------------------------
  const loadOverwolfProfile = async (): Promise<void> => {
    const user = await getCurrentOverwolfUser();

    if (!user) {
      setCurrentUser(null);
      setIsLoggedIn(false);
      setLoginStateChangedEvent({
        status: 'error',
        connectionStatus: 'Offline',
        relayData: null,
        suspended: false,
        token: null,
        username: null,
      });
      setLoginCheckCompleted(true);
      return;
    }

    // overwolf.profile.getCurrentUser exposes only email hashes, so pull the
    // plaintext email from the store bridge's profile details
    // (GetUserProfileDetails -> OverwolfStore.login.getCurrentUser) for display.
    const details = await getOverwolfCurrentUser();
    const email =
      typeof details?.profile?.email === 'string' ? details.profile.email : '';

    // No REST token in this mode — installed-state and downloads go over the
    // bridge, which doesn't need it.
    setCurrentUser({ profile: mapOverwolfProfile(user, email), token: '' });
    setIsLoggedIn(true);

    if (user.username) {
      analytics.setUserName(user.username);
    }

    setLoginStateChangedEvent({
      status: 'success',
      connectionStatus: 'Online',
      relayData: null,
      suspended: false,
      token: '',
      username: user.username,
    });

    setLoginCheckCompleted(true);
  };

  // ---------------------------------------------------------------------------
  const resendVerification = async (email: string): Promise<boolean> => {
    try {
      const url = `${kResendVerificationEndpoint}&email=${email}`;
      const result = await fetch(url);
      log.info(`resend email success: ${JSON.stringify(result)}`);
      return true;
    } catch (e) {
      log.error(`resend email error: ${JSON.stringify(e)}`);
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  const performOverwolfLogin = async (
    email: string,
    password: string,
  ): Promise<ILoginResult> => {
    return loginService.performOverwolfLogin(email, password);
  };

  // ---------------------------------------------------------------------------
  const performSocialLogin = (network: SocialNetwork): void => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.append(kSocialCallbackParam, 'true');
    currentUrl.searchParams.append(kSocialNetworkParam, network);
    const callbackUrl = encodeURIComponent(`${currentUrl.toString()}`);
    const url = `${kSocialLoginEndpoint}/${network}?${kLoginRedirectParam}=${callbackUrl}`;
    window.location.href = url;
  };

  // ---------------------------------------------------------------------------
  const performOverwolfSignUp = async (
    signUpParams: SignUpParams,
  ): Promise<SignUpResult> => {
    return loginService.performOverwolfSignUp(signUpParams);
  };

  // ---------------------------------------------------------------------------
  const logout = (): void => {
    if (isOverwolfClient) {
      // Log out via the shell bridge, then refresh auth-dependent UI by
      // re-reading the profile. (A client login-state event may also fire and
      // re-read — that's idempotent, so no special-casing needed.)
      void requestOverwolfLogout()
        .then(() => loadOverwolfProfile())
        .catch(() => undefined);
      return;
    }

    loginService.logOut();
    setCurrentUser(null);
    setIsLoggedIn(false);
    setLoginStateChangedEvent({
      status: 'error',
      connectionStatus: 'Offline',
      relayData: null,
      suspended: false,
      token: null,
      username: null,
    });
  };

  // ---------------------------------------------------------------------------
  const resetPassword = async (
    email: string,
  ): Promise<IResetPasswordResult> => {
    return loginService.resetPassword(email);
  };

  // ---------------------------------------------------------------------------
  const cancelAccountDeletion = async () => {
    const response = await loginService.restoreAccount();
    if (!response) {
      return;
    }

    await getAndSetProfile(true);
  };

  // ---------------------------------------------------------------------------
  const setToken = (token: string): void => {
    loginService.setToken(token);
  };

  // ---------------------------------------------------------------------------
  const getUserProfile = async (token: string, refreshRequired = false) => {
    try {
      const userProfile = await loginService.getUserProfile(
        token,
        refreshRequired,
      );
      const profileResult = userProfile.profile;
      return { profile: profileResult, status: 'success', token };
    } catch (error) {
      log.error(`get profile error: ${JSON.stringify(error)}`);
      return { profile: null, status: 'error', token };
    }
  };

  // ---------------------------------------------------------------------------
  const getHashedEmailObject = (): IHashedEmail => {
    if (!currentUser.profile.email) {
      return null;
    }
    const emailRegex = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,63}$/;
    if (!emailRegex.test(currentUser.profile.email)) {
      return null;
    }

    const at = currentUser.profile.email.indexOf('@');
    if (at < 1 || at === currentUser.profile.email.length - 1) {
      return null;
    }

    let email = currentUser.profile.email.trim().toLowerCase();
    const [local, domain] = email.split('@');

    if (domain === 'gmail.com') {
      let normalizedLocal = local.replace(/\./g, '');
      const plusIndex = normalizedLocal.indexOf('+');
      if (plusIndex >= 0) {
        normalizedLocal = normalizedLocal.substring(0, plusIndex);
      }

      email = `${normalizedLocal}@${domain}`;
    }

    const sha1 = SHA1(email)?.toString();
    const sha256 = SHA256(email)?.toString();
    const md5 = MD5(email)?.toString();
    return { md5, sha1, sha256 };
  };

  // ---------------------------------------------------------------------------
  const waitForLoginCheck = (): Promise<void> => {
    if (loginCheckCompleted) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      loginCheckResolvers.current.push(resolve);
    });
  };

  // ---------------------------------------------------------------------------
  const trackLoginSuccess = (): void => {
    const isLoginRedirect =
      window.location.search.includes(kSocialCallbackParam);
    if (!isLoginRedirect) {
      return;
    }

    setIsPostLogin(true);

    const network = new URL(window.location.href).searchParams.get(
      kSocialNetworkParam,
    );

    analytics.track('appstore_login_success', {
      network: network ?? 'unknown',
    });

    const url = new URL(window.location.href);
    url.searchParams.delete(kSocialCallbackParam);
    url.searchParams.delete(kSocialNetworkParam);
    window.history.replaceState({}, document.title, url.toString());
  };

  // ---------------------------------------------------------------------------
  const value: UserProfileContextValue = useMemo(
    () => ({
      isLoggedIn,
      currentUser,
      loginStateChangedEvent,
      isPostLogin,
      resendVerification,
      getAndSetProfile,
      performOverwolfSignUp,
      performOverwolfLogin,
      performSocialLogin,
      logout,
      resetPassword,
      cancelAccountDeletion,
      setToken,
      getUserProfile,
      getHashedEmailObject,
      waitForLoginCheck,
    }),
    [
      isPostLogin,
      isLoggedIn,
      currentUser,
      loginStateChangedEvent,
      loginCheckCompleted,
      isOverwolfClient,
    ],
  );

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

// -----------------------------------------------------------------------------
export const useUserProfile = (): UserProfileContextValue => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
